import { Dispatch } from "redux";
import { setNodes, setCharts, setFields, setConsultantsFormValues, setConsultantsFieldValues, setManagers } from "./reducer";
import { toast } from "react-toastify";
import { BOSS_MAN_NAME, D3OrgChartNode, Department, DocumentFields, OrgChartNode } from "models/org-chart-node";
import ConsultantFields from "Components/organisms/forms/contractor-form/types";
import Employee from "models/employee";
import Recruit from "models/recruit";
import { PaginatedResult } from "models/queryable";
import { buildQueryString } from "utils/query-string";


export const getOrgChart = () => async (dispatch: Dispatch) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/org-chart`;
        const res = await fetch(url, {
            credentials: 'include'
        })



        if (!res.ok) {
            throw new Error("HTTP Error")
        }

        const nodes = await res.json();

        dispatch(setNodes(transformNodes(nodes)));
    } catch (err: any) {
        toast.error("Error getting org chart nodes.")
        console.error("Error getching org chart nodes:", err);
        return []
    }
}

export const setConsultantFormValues = (field: keyof ConsultantFields, value: any) => (dispatch: Dispatch, getState: any) => {
    dispatch(setConsultantsFieldValues({
        key: field, value
    }));
}




export const getCharts = () => async (dispatch: Dispatch) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/org-chart/charts`
        const res = await fetch(url, {
            credentials: 'include'
        })

        if (!res.ok) {
            throw new Error("HTTP Error")
        }
        const data = await res.json()

        // Synthetic "whole org" entry — has no departmentHeadId, so reduceNodes treats
        // it the same as no chart selected (full tree) instead of filtering to one head.
        dispatch(setCharts([...data.data, {
            id: 'root',
            code: 'ROOT',
            description: 'Organization Chart',
            departmentLevelId: '',
        }]))
    } catch (err: any) {

        toast.error("Error getting charts.")
        console.error("Error getting charts:", err);
        return [];
    }
}

const transformNodes = (nodes: OrgChartNode[]): D3OrgChartNode[] => {
    let res = nodes.map((emp: OrgChartNode) => ({
        id: emp.id,
        name: empFullName(emp),
        title: emp.jobTitle?.description ?? null,
        department: emp.department?.description ?? null,
        parentId: emp.managerId ?? '',
        size: '',
        employeeType: emp.employeeType?.name ?? "Employee"
    }))

    return res.filter((n: D3OrgChartNode) => n.parentId || BOSS_MAN_NAME === n.name);
}


export const reduceNodes = (tree: Map<string, D3OrgChartNode[]>, orgChartNodes: D3OrgChartNode[], chart: Department | null): D3OrgChartNode[] => {
    if (!chart || !chart.headId || orgChartNodes.length === 0) return orgChartNodes


    const h = orgChartNodes.find(n => n.id === chart.headId);

    // If this is not found. THis means that the chart does not exist
    if (!h) {
        toast.error("Head not found. Chart invalid")
        return [];
    }

    // Readonly fix
    let headNode = JSON.parse(JSON.stringify(h))

    headNode.parentId = '';
    const stack: string[] = [h.id];
    let res: D3OrgChartNode[] = [headNode];

    while (stack.length > 0) {
        const curr: string | undefined = stack.pop();

        if (!curr) break;

        const children = tree.get(curr);

        if (children) {
            res = [...res, ...children]
            // Add chilren to stack
            children.forEach(c => stack.push(c.id));
        }
    }
    return res;
}

export const reduceChildMap = (tree: Map<string, D3OrgChartNode[]>): Map<string, boolean> => {
    const childMap = new Map<string, boolean>();

    const keys = tree.keys();
    keys.forEach(k => childMap.set(k, true))

    return childMap
}

export const biuldOrgChartTree = (orgChartNodes: D3OrgChartNode[]): Map<string, D3OrgChartNode[]> => {
    const tree: Map<string, D3OrgChartNode[]> = new Map()

    // Build Tree
    for (let i = 0; i < orgChartNodes.length; i++) {
        if (!tree.has(orgChartNodes[i].parentId))
            tree.set(orgChartNodes[i].parentId, [])
        // If Redux is being stubborn about immutability
        tree.set(orgChartNodes[i].parentId, [...(tree.get(orgChartNodes[i].parentId) ?? []), orgChartNodes[i]]);
    }

    return tree;
}


const empFullName = (node: OrgChartNode): string => {
    return `${node.commonName ?? node.firstName} ${node.lastName}`.trim()
}



export const viewDocument = async (documentId: string, genDocId?: string): Promise<boolean> => {
    try {
        const url = genDocId
            ? `${process.env.REACT_APP_API_URL}/documents/view/${documentId}/${genDocId}`
            : `${process.env.REACT_APP_API_URL}/documents/view/${documentId}`;
        const res = await fetch(url, {
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }

        // Get filename from Content-Disposition header or use default
        const contentDisposition = res.headers.get('Content-Disposition');
        let filename = 'document.docx';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Convert response to blob
        const blob = await res.blob();

        // Create download link and trigger download
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        return true;
    } catch (err: any) {
        return false;
    }
}


export const getFields = () => async (dispatch: Dispatch): Promise<DocumentFields | null> => {
    try {

        const url = `${process.env.REACT_APP_API_URL}/documents/fields`;
        const res = await fetch(url, {
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }
        const fields = await res.json();
        dispatch(setFields(fields));
        return fields;
    } catch (err: any) {
        console.error('Error fetching documents:', err);
        return null;
    }
}

export const getManagers = () => async (dispatch: Dispatch): Promise<Employee[]> => {
    try {

        const url = `${process.env.REACT_APP_API_URL}/employee/hiring-managers`;
        const res = await fetch(url, {
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        dispatch(setManagers(data.data));
        return data.data;
    } catch (err: any) {
        console.error('Error fetching documents:', err);
        return [];
    }
}

export const searchRecruits = async (name: string): Promise<Recruit[]> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/recruits${buildQueryString({ name })}`;
        const res = await fetch(url, {
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data: { data: PaginatedResult<Recruit> } = await res.json();
        return data.data.data;
    } catch (err: any) {
        console.error('Error searching recruits:', err);
        return [];
    }
}

export const getRecruit = async (id: string): Promise<Recruit | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/recruits/${id}`;
        const res = await fetch(url, {
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data: { data: Recruit } = await res.json();
        return data.data;
    } catch (err: any) {
        console.error('Error getting recruit:', err);
        return null;
    }
}

// Starts the Lambda; returns true when it was triggered (the page polls the sync log).
export const syncDayforceData = async (): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/employee/sync`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST'
        });

        if (!res.ok) {
            const errRes = await res.json();
            toast.error(errRes.message);
            return false
        }
        toast.success("Sync started. The page will refresh automatically when it finishes.")
        return true
    } catch (err: any) {

        toast.error("Something went wrong. Could not sync data")
        console.error('Error syncing data:', err);
        return false
    }
} 